/**
 * 메뉴 권한 게이트 훅 — 현재 사용자 권한(/auth/me/permissions) + 역할(관리자 여부)로 {@link MenuGate} 구성.
 *
 * 사이드바(AppShell)·생성/수정/삭제 버튼 등 어디서나 `const gate = useMenuGate()` 로 재사용한다(admin 판정 중복 제거).
 * 서버 최종 권위는 아이템 CRUD 게이트(PermissionCheckService)이며 이 훅은 UX 보조임.
 */
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useMyPermissions } from "@/lib/queries/useRoles";
import { useWorkitemTypes } from "@/lib/queries/useWorkitemTypes";
import { useUserStore } from "@/store/user-store";

import { isAdminRoleCode } from "./roleCodes";
import { type MenuGate, makeMenuGate } from "./menuPermissions";

export function useMenuGate(): MenuGate {
  // 역할 코드는 dev·운영 모두 정확(서버 권위) — 관리자 판정 기준. dev 인증필터의 부풀린 권한과 무관.
  const roleCode = useUserStore((s) => s.user?.roleCode);
  // 권한은 워크스페이스 단위 — 현재 워크스페이스 슬러그로 게이팅(URL 밖이면 undefined → 회사공통 폴백).
  const { workspace } = useParams<{ workspace: string }>();
  const { data: perms } = useMyPermissions(workspace);
  // 모듈 판정(§5.2)용 유형→모듈 매핑(대문자). 목록 로딩 전에는 undefined(허용 폴백).
  const { data: workitemTypes } = useWorkitemTypes(workspace);
  const typeModules = useMemo(
    () =>
      workitemTypes
        ? new Map(workitemTypes.map((t) => [t.typeCode.toUpperCase(), t.moduleCode.toUpperCase()]))
        : undefined,
    [workitemTypes],
  );
  return useMemo(
    () => makeMenuGate(perms, isAdminRoleCode(roleCode), typeModules),
    [perms, roleCode, typeModules],
  );
}

/**
 * 현재 사용자 메뉴 권한 수신 완료 여부. 수신 전 게이트는 허용 폴백이므로, 진입 가드·기본 진입 경로는
 * 이 값이 true 가 된 뒤에 판정한다(권한 없음 깜빡임·잘못된 리다이렉트 방지). 조회 실패도 완료로 본다(서버가 최종 차단).
 */
export function useMenuPermsReady(): boolean {
  const { workspace } = useParams<{ workspace: string }>();
  return !useMyPermissions(workspace).isPending;
}
